import type { FunctionalRole, UserProfile } from '../types';

/**
 * Rechte einer Person im Frontend (docs/07-organisation-und-aggregation.md, Kapitel 3).
 * Die Oberfläche blendet nur aus; durchgesetzt werden die Rechte von Firestore Rules und Cloud Functions.
 */
export interface Permissions {
    orgUnitId: string | null;
    roles: FunctionalRole[];
    isSuperAdmin: boolean;
    isAssessor: boolean;
    /** Add-on: Struktur, Personen, Einstellungen, Querschnittsthemen (keine Inhalte fremder Einheiten) */
    isTenantAdmin: boolean;
    /** Add-on: Personen der eigenen Einheit verwalten */
    isOrgAdmin: boolean;
    /** QM-Management oder Management: CAF-Assessments, Aggregate und Vergleiche der eigenen Einheit */
    canUseCaf: boolean;
    /** QM-Management: Assessments anlegen, abschliessen, freigeben, löschen */
    canManageAssessments: boolean;
    /** Portfoliomanagement oder Management: Portfolio der eigenen Einheit sehen */
    canSeePortfolio: boolean;
    /** Portfoliomanagement: priorisieren, weitergeben, Themen zuordnen, eigene Themenbereiche */
    canManagePortfolio: boolean;
    /** Portfoliomanagement oder Projektleitung: Projekte anlegen */
    canCreateProjects: boolean;
    /** Irgendeine Projektrolle: Projekte, in denen die Person mitwirkt, und eigene Massnahmen */
    canUseProjects: boolean;
}

export function derivePermissions(profile: UserProfile | null | undefined): Permissions {
    const roles = (Array.isArray(profile?.roles) ? profile.roles : []) as FunctionalRole[];
    const isSuperAdmin = profile?.role === 'super_admin';
    const has = (...wanted: FunctionalRole[]) => isSuperAdmin || wanted.some(r => roles.includes(r));

    return {
        orgUnitId: profile?.orgUnitId || null,
        roles,
        isSuperAdmin,
        isAssessor: profile?.role === 'assessor',
        isTenantAdmin: isSuperAdmin || profile?.isTenantAdmin === true,
        isOrgAdmin: profile?.isOrgAdmin === true,
        canUseCaf: has('qm_management', 'management'),
        canManageAssessments: has('qm_management'),
        canSeePortfolio: has('portfolio_management', 'management'),
        canManagePortfolio: has('portfolio_management'),
        canCreateProjects: has('portfolio_management', 'project_management'),
        canUseProjects: has('portfolio_management', 'management', 'project_management', 'project_team'),
    };
}

/**
 * Hat eine Person ohne Mitgliedschaft Zugriff auf ein Projekt (inkl. Massnahmen)?
 * Portfoliomanagement und Management der Herkunftseinheit (docs/07, Kapitel 3.3).
 */
export function hasImplicitProjectAccess(
    user: Pick<UserProfile, 'orgUnitId' | 'roles'> | undefined,
    project: { originOrgUnitId?: string } | null | undefined
): boolean {
    if (!user || !project?.originOrgUnitId || user.orgUnitId !== project.originOrgUnitId) return false;
    const roles = Array.isArray(user.roles) ? user.roles : [];
    return roles.includes('portfolio_management') || roles.includes('management');
}

/**
 * Darf eine Person als Verantwortliche einer Massnahme eingetragen sein? Entspricht canWorkOnProject in
 * firestore.rules: Projektleitung, Mitglieder, persönlich Zugewiesene sowie Portfoliomanagement und
 * Management der Herkunftseinheit.
 */
export function isValidTaskAssignee(
    uid: string,
    tenantUsers: Pick<UserProfile, 'uid' | 'orgUnitId' | 'roles'>[],
    project: { originOrgUnitId?: string; responsibleId?: string; memberIds?: string[]; assignedUsers?: string[] }
): boolean {
    const user = tenantUsers.find(u => u.uid === uid);
    if (!user) return false;
    return project.responsibleId === uid ||
        !!project.memberIds?.includes(uid) ||
        !!project.assignedUsers?.includes(uid) ||
        hasImplicitProjectAccess(user, project);
}
